// @ts-nocheck
// Agent Portal (/cti2) — Dialer helpers split out of Dialer.tsx: recent-calls
// row shaping and its own timestamp formatter. Pure/derived, no store reads
import { useMemo } from 'react';
import { FiArrowDownLeft, FiArrowUpRight, FiPhoneMissed, FiPhoneOff } from 'react-icons/fi';
import { callOutcome, isIncomingDirection, callRowCustomerNumber, callDurationSeconds, formatSeconds } from '@/features/cti/ctiHelpers';
import { CALL_OUTCOME_LABELS } from '@/utilities/callStatus';
import { getUser } from '@/utilities/setting';
import { buildCallTypeBadges } from '@/utilities/callTypeBadges';
import { formatClock } from './format';
import notificationSound from '@/assets/sound/notificationSound.mp3';
import { trackSound } from '../audio/audioDevices';
import { getApiCall } from '@/service/httpCall';
import { API_NOTIFICATION_LIST } from '@/service/url';

export const formatWorkDuration = formatClock;

export function sanitizeDialInput(raw) {
  return String(raw || '').replace(/(?!^\+)[^\d]/g, '');
}

export function normalizePastedDial(raw, callingCode = '91') {
  const d = String(raw || '').replace(/\D/g, '');
  const cc = String(callingCode || '').replace(/\D/g, '');
  if (cc && d.length === cc.length + 10 && d.startsWith(cc)) return d.slice(cc.length);
  if (d.length === 11 && d.startsWith('0')) return d.slice(1);
  return d;
}

// Server-side masked numbers ("7015XXXXXX", "7015****") can't be dialed.
export function isMaskedNumber(value) {
  return /[xX*]/.test(String(value || ''));
}

// Standard DTMF dual-tone frequency pairs (Hz) per key. demo-1.ts's own
// keypad click handler sends the real SIP DTMF signal to the far end
// during a call, but nothing played an audible tone back to the AGENT —
// generated locally via Web Audio rather than shipping 12 audio files.
const DTMF_FREQUENCIES = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

let dtmfAudioCtx = null;
export function playDtmfTone(digit) {
  const pair = DTMF_FREQUENCIES[String(digit)];
  if (!pair) return;
  try {
    if (!dtmfAudioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      dtmfAudioCtx = trackSound(new Ctx());
    }
    if (dtmfAudioCtx.state === 'suspended') dtmfAudioCtx.resume().catch(() => {});
    const ctx = dtmfAudioCtx;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.setValueAtTime(0.12, now + 0.1);
    gain.gain.linearRampToValueAtTime(0, now + 0.14);
    gain.connect(ctx.destination);
    pair.forEach((freq) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.15);
    });
  } catch (e) { /* audio not available — silent keypad, no crash */ }
}

const notificationAudio = typeof Audio !== 'undefined' ? trackSound(new Audio(notificationSound)) : null;
if (notificationAudio) {
  notificationAudio.preload = 'auto';
  const unlock = () => {
    notificationAudio.muted = true;
    notificationAudio.play()
      .then(() => { notificationAudio.pause(); notificationAudio.currentTime = 0; })
      .catch(() => {})
      .finally(() => { notificationAudio.muted = false; });
    ['click', 'keydown', 'touchstart'].forEach((ev) => document.removeEventListener(ev, unlock));
  };
  ['click', 'keydown', 'touchstart'].forEach((ev) => document.addEventListener(ev, unlock));
}

let assignedNotificationsInFlight: Promise<any> | null = null;
export function fetchAssignedNotifications() {
  if (!assignedNotificationsInFlight) {
    assignedNotificationsInFlight = getApiCall(API_NOTIFICATION_LIST, { scope: 'assigned' })
      .finally(() => { assignedNotificationsInFlight = null; });
  }
  return assignedNotificationsInFlight;
}

const NOTIFICATION_POLL_MS = 120000;
const NOTIFICATION_STALE_MS = 60000;
const notificationWatchers = new Set<() => void>();
let notificationTimer: ReturnType<typeof setInterval> | null = null;
let notificationHiddenAt = 0;
const runNotificationWatchers = () => notificationWatchers.forEach((fn) => fn());
const onNotificationVisibility = () => {
  if (document.hidden) {
    notificationHiddenAt = Date.now();
  } else if (notificationHiddenAt && Date.now() - notificationHiddenAt >= NOTIFICATION_STALE_MS) {
    runNotificationWatchers();
  }
};

export function watchNotifications(fetchFn) {
  notificationWatchers.add(fetchFn);
  window.addEventListener('cti:notification_refresh', fetchFn);
  if (!notificationTimer) {
    notificationTimer = setInterval(() => { if (!document.hidden) runNotificationWatchers(); }, NOTIFICATION_POLL_MS);
    document.addEventListener('visibilitychange', onNotificationVisibility);
  }
  return () => {
    notificationWatchers.delete(fetchFn);
    window.removeEventListener('cti:notification_refresh', fetchFn);
    if (!notificationWatchers.size && notificationTimer) {
      clearInterval(notificationTimer);
      notificationTimer = null;
      document.removeEventListener('visibilitychange', onNotificationVisibility);
    }
  };
}

export function playNotificationTone() {
  if (!notificationAudio) return;
  notificationAudio.currentTime = 0;
  notificationAudio.volume = 1;
  notificationAudio.play().catch(() => { /* autoplay blocked — silent, no crash */ });
}

const lastTenDigits = (v) => {
  const digits = String(v || '').replace(/\D/g, '');
  return digits ? digits.slice(-10) : '';
};

const recordTimeMs = (r) => {
  const t = new Date(r?.time_of_call || r?.created_at || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * The same physical outbound call can land in the `calls` table as two
 * separate rows with two DIFFERENT `call_sid` values — one written by the
 * CDR webhook pipeline (keyed off the carrier's sipCallId, no resolved
 * contact name yet) and one written by this app's own post-hangup POST
 * (keyed off the agent softphone's local SIP Call-ID header, with the
 * resolved contact/disposition name). Since the call_sid genuinely
 * differs, no call_sid/id dedupe can catch this — it has to be merged by
 * matching direction + number + a close timestamp instead. Prefers the
 * row with a resolved name, tie-breaking on the longer billing duration
 * (the CDR row is normally the more accurate one).
 */
const CALL_TYPE_FLAG_FIELDS = [
  'is_transfer', 'is_conference', 'is_ivr', 'is_agent_lead',
  'is_barge_in', 'is_whisper', 'is_monitor', 'is_follow_up', 'is_voicemail', 'is_missed_ivr',
];
const CALL_TYPE_ID_FIELDS = [
  'campaign_id', 'call_group_id', 'campaign_name', 'call_group_name', 'did',
  'trace_id', 'call_status', 'other_party_call_status',
];
const CALL_TYPE_MAX_FIELDS = [
  'patch_duration', 'hold_duration', 'mute_duration',
  'wrap_up_seconds', 'total_duration', 'other_party_total_duration',
];
const CDR_FIRST_FIELDS = ['bridge_ring_duration', 'other_party_ring_duration', 'billing_duration'];

function carryCallTypeFields(target, ...sources) {
  if (!target) return;
  for (const field of CALL_TYPE_FLAG_FIELDS) {
    if (sources.some((s) => s?.[field] === true)) target[field] = true;
  }
  for (const field of CALL_TYPE_ID_FIELDS) {
    if (target[field]) continue;
    const found = sources.find((s) => s?.[field]);
    if (found) target[field] = found[field];
  }
  for (const field of CALL_TYPE_MAX_FIELDS) {
    const best = Math.max(
      Number(target[field]) || 0,
      ...sources.map((s) => Number(s?.[field]) || 0)
    );
    if (best > 0) target[field] = best;
  }
  for (const field of CDR_FIRST_FIELDS) {
    const cdrRow = sources.find((s) => s && !s.agent_profile_type && s[field] != null);
    if (cdrRow) target[field] = cdrRow[field];
    else if (target[field] == null) target[field] = sources.find((s) => s?.[field] != null)?.[field] ?? target[field];
  }
  const media = [];
  const seenMedia = new Set();
  for (const source of [target, ...sources]) {
    for (const item of source?.media || []) {
      const keys = [`${item?.kind}-${item?.id}`, `${item?.kind}-f-${item?.blob_path || item?.name || ''}`];
      if (keys.some((k) => seenMedia.has(k) && !k.endsWith('-f-'))) continue;
      keys.forEach((k) => seenMedia.add(k));
      media.push(item);
    }
  }
  if (media.length) target.media = media;
}

const END_TIME_TOLERANCE_MS = 5_000;
// Two rows with different trace_ids are two different calls, however close in
// time — merging them glued one call's recording onto the other.
const differentTrace = (a, b) => Boolean(a?.trace_id && b?.trace_id && a.trace_id !== b.trace_id);
function dedupeAgentLegRows(records) {
  const out = [];
  const used = new Set();
  for (let i = 0; i < records.length; i++) {
    if (used.has(i)) continue;
    const r = records[i];
    const endA = new Date(r?.end_time || 0).getTime();
    let partnerIdx = -1;
    if (endA) {
      for (let j = i + 1; j < records.length; j++) {
        if (used.has(j)) continue;
        const o = records[j];
        if ((r?.direction || null) === (o?.direction || null)) continue;
        const endB = new Date(o?.end_time || 0).getTime();
        if (!endB || Math.abs(endB - endA) > END_TIME_TOLERANCE_MS) continue;
        if (Boolean(r?.agent_profile_type) === Boolean(o?.agent_profile_type)) continue;
        if (differentTrace(r, o)) continue;
        partnerIdx = j;
        break;
      }
    }
    if (partnerIdx === -1) { out.push(r); continue; }
    used.add(partnerIdx);
    const partner = records[partnerIdx];
    const winner = { ...(r?.agent_profile_type ? r : partner) };
    carryCallTypeFields(winner, r, partner);
    out.push(winner);
  }
  return out;
}

export function mergeSameCallRows(records, windowMs = 90_000) {
  const deduped = dedupeAgentLegRows(records);
  const merged = [];
  for (const r of deduped) {
    const dir = r?.direction || null;
    // Falling back across BOTH caller_number and callee_number (whichever
    // was non-empty) used to be the actual bug: for an outgoing mobile
    // call whose CDR-webhook row has an empty callee_number, that fell
    // back to caller_number — the AGENT'S OWN number, not the customer's.
    // Two different outbound calls (to different customers) placed within
    // the merge window then BOTH computed the same dedupe key (the
    // agent's own number) and got merged into one row, with one call's
    // resolved contact name silently overwriting the other's ("call
    // history shows contact A's name on what was actually a call to B").
    // Only ever use the field that's actually the CUSTOMER'S number for
    // this call's direction — if that's empty, leave this row unmerged
    // (shown twice, in the rare case) rather than risk merging it with an
    // unrelated call under a false shared key.
    const isOutgoing = dir === 'outgoing' || dir === 'outbound' || dir === 'out';
    const customerNumberRaw = r?.number || (isOutgoing ? r?.callee_number : r?.caller_number);
    const num = lastTenDigits(customerNumberRaw);
    const time = recordTimeMs(r);
    const dupIdx = num
      ? merged.findIndex((m) => m.__dir === dir && m.__num === num && Math.abs(m.__time - time) <= windowMs && !differentTrace(m, r))
      : -1;
    if (dupIdx === -1) {
      merged.push({ ...r, __num: num, __dir: dir, __time: time });
      continue;
    }
    const existing = merged[dupIdx];
    const existingHasName = Boolean(existing?.name || existing?.caller_profile);
    const currentHasName = Boolean(r?.name || r?.caller_profile);
    // Same idea one step earlier: for an unanswered call both duplicate
    // rows tie at billing_duration 0, so that tie-break has no signal —
    // prefer whichever row actually resolved a number first (the CDR-
    // webhook row can be written with callee_number empty), before ever
    // falling through to the duration check.
    const existingHasNumber = Boolean(existing?.number);
    const currentHasNumber = Boolean(r?.number);
    const preferCurrent = currentHasName && !existingHasName
      ? true
      : currentHasName !== existingHasName
        ? false
        : currentHasNumber && !existingHasNumber
          ? true
          : currentHasNumber !== existingHasNumber
            ? false
            : Number(r?.billing_duration || 0) > Number(existing?.billing_duration || 0);
    if (preferCurrent) merged[dupIdx] = { ...r, __num: num, __dir: dir, __time: time };
    const profileType = existing?.agent_profile_type || r?.agent_profile_type || '';
    if (profileType) merged[dupIdx].agent_profile_type = profileType;
    carryCallTypeFields(merged[dupIdx], existing, r);
  }
  // Neither this loop nor any caller ever explicitly sorted by actual call
  // time — records were shown in whatever order the API/backend happened
  // to return them (created_at, not the call's own start_time/time_of_call,
  // which can drift apart when CDR processing lags), so "latest at top"
  // wasn't actually guaranteed. __time already captures the real call time.
  merged.sort((a, b) => b.__time - a.__time);
  return merged.map(({ __num, __dir, __time, ...rest }) => rest);
}

export function formatCallLogTimestamp(value) {
  if (!value) return '--:--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--';
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const time = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Today · ${time}`;
  return `${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · ${time}`;
}

export function useRecentCalls(agentCallsData, contactNameByNumber) {
  const currentUserAccess = useMemo(() => {
    try { return getUser()?.agentData?.access || null; } catch { return null; }
  }, []);
  return useMemo(() => {
    const records = agentCallsData?.callRecords || [];
    return records.map((record) => {
      const isIncoming = isIncomingDirection(record);
      const outcome = callOutcome(record);
      const isAnswered = outcome === 'answered';
      const num = callRowCustomerNumber(record) || 'Unknown';
      // For a masked agent `number` arrives already masked ("7015XXXXXX"),
      // so redialing it dialed only the visible digits. dial_number is the
      // unmasked number sent alongside it; the UI keeps showing it masked.
      const dialNum = record?.dial_number || (isMaskedNumber(num) || num === 'Unknown' ? null : num);
      // The backend's own name resolution (record.name/caller_profile) is
      // empty for most calls, showing "Unknown" even when the number
      // matches one of the agent's own saved contacts — cross-reference
      // that list (fetched once in AgentPortalPage.tsx) as a fallback
      // before giving up and actually calling it Unknown.
      const contactName = contactNameByNumber?.[String(dialNum || num).replace(/\D/g, '').slice(-10)];
      return {
        id: record?.id,
        icon: isAnswered
          ? (isIncoming ? <FiArrowDownLeft /> : <FiArrowUpRight />)
          : (outcome === 'abandoned' ? <FiPhoneMissed /> : <FiPhoneOff />),
        colorClass: isAnswered ? (isIncoming ? 'text-success' : 'text-primary') : 'text-destructive',
        // A resolved name means this number matched a saved contact —
        // used to tell saved vs unsaved numbers apart at a glance.
        isSavedContact: Boolean(record?.name || record?.caller_profile || contactName),
        name: record?.name || record?.caller_profile || contactName || 'Unknown',
        num,
        dialNum,
        unanswered: !isAnswered,
        dur: isAnswered
          ? formatSeconds(callDurationSeconds(record))
          : CALL_OUTCOME_LABELS[outcome],
        time: formatCallLogTimestamp(record?.time_of_call || record?.created_at),
        rawTime: record?.time_of_call || record?.created_at || null,
        callSid: record?.call_sid || null,
        fallbackRemark: record?.disposition_note || record?.note || record?.remark || '',
        typeBadges: buildCallTypeBadges(record, {
          access: currentUserAccess,
          includeProfileType: true,
        }),
      };
    });
  }, [agentCallsData, currentUserAccess]);
}
